import React, { useState } from 'react';
import styled from '@emotion/styled';
import { theme } from '../../styles/theme';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUp, ArrowDown } from 'react-feather';

const TableContainer = styled.div`
  width: 100%;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  border: 1px solid rgba(0, 0, 0, 0.08);
  // height: calc(100vh - 50px);
  display: flex;
  flex-direction: column;
`;

const TableWrapper = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${theme.colors.background};
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 1200px;
`;

const Th = styled.th`
  text-align: left;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.surface};
  color: ${theme.colors.primary};
  font-weight: 600;
  font-size: 0.813rem;
  border-bottom: 2px solid ${theme.colors.primary}20;
  position: sticky;
  top: 0;
  z-index: 2;
  cursor: ${props => props.sortable ? 'pointer' : 'default'};
  user-select: none;
  white-space: nowrap;
  transition: all 0.2s ease;
  height: 40px;
  
  &:first-of-type {
    padding-left: ${theme.spacing.lg};
  }
  
  &:last-of-type {
    padding-right: ${theme.spacing.lg};
  }
  
  &:hover {
    ${props => props.sortable && `
      background: ${theme.colors.primary}08;
    `}
  }

  .sort-icon {
    display: inline-flex;
    vertical-align: middle;
    margin-left: ${theme.spacing.xs};
  }
`;

const Td = styled.td`
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  color: ${theme.colors.text.primary};
  font-size: 0.813rem;
  vertical-align: middle;
  height: 48px;
  
  &:first-of-type {
    padding-left: ${theme.spacing.lg};
  }
  
  &:last-of-type {
    padding-right: ${theme.spacing.lg};
  }
`;

const Tr = styled.tr`
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.primary}04;
    
    ${Td} {
      border-bottom-color: ${theme.colors.primary}10;
    }
  }

  &:last-child ${Td} {
    border-bottom: none;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: ${theme.colors.background};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${theme.spacing.md};
  }
`;

const PageInfo = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.body2.fontSize};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};

  select {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    border: 1px solid rgba(0, 0, 0, 0.23);
    border-radius: ${theme.borderRadius.sm};
    background: white;
    cursor: pointer;
    font-size: ${theme.typography.body2.fontSize};
    color: ${theme.colors.text.primary};
    
    &:hover {
      border-color: ${theme.colors.primary};
    }
    
    &:focus {
      border-color: ${theme.colors.primary};
      outline: none;
      box-shadow: 0 0 0 2px ${theme.colors.primary}20;
    }
  }
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
`;

const PageButton = styled.button`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  min-width: 32px;
  height: 32px;
  border: 1px solid ${props => props.active ? theme.colors.primary : 'rgba(0, 0, 0, 0.12)'};
  background: ${props => props.active ? theme.colors.primary : 'transparent'};
  color: ${props => props.active ? 'white' : theme.colors.text.primary};
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? theme.colors.primary : theme.colors.primary}08;
    border-color: ${theme.colors.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl} ${theme.spacing.xl};
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${theme.colors.primary}20;
  border-top-color: ${theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const CellContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const Table = ({
  columns,
  data,
  pageSize = 10,
  loading,
  currentPage,
  onPageChange,
}) => {
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  const handleRowsPerPageChange = (e) => {
    const newRowsPerPage = parseInt(e.target.value);
    setRowsPerPage(newRowsPerPage);
    onPageChange(1);
  };

  const renderSortIcon = (column) => {
    if (!column.sortable) return null;
    
    if (sortConfig.key === column.key) {
      return (
        <span className="sort-icon">
          {sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
        </span>
      );
    }
    return null;
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;
    
    buttons.push(
      <PageButton
        key="first"
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
        title="First page"
      >
        <ChevronsLeft />
      </PageButton>
    );

    buttons.push(
      <PageButton
        key="prev"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        title="Previous page"
      >
        <ChevronLeft />
      </PageButton>
    );

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <PageButton
          key={i}
          active={currentPage === i}
          onClick={() => onPageChange(i)}
        >
          {i}
        </PageButton>
      );
    }

    buttons.push(
      <PageButton
        key="next"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        title="Next page"
      >
        <ChevronRight />
      </PageButton>
    );

    buttons.push(
      <PageButton
        key="last"
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
        title="Last page"
      >
        <ChevronsRight />
      </PageButton>
    );

    return buttons;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderCellContent = (column, row) => {
    if (column.render) {
      return column.render(row);
    }

    const value = row[column.key];

    // Format dates
    if (column.key.toLowerCase().includes('date')) {
      return formatDate(value);
    }

    // Format durations
    if (column.key === 'duration') {
      return `${value} days`;
    }

    return value;
  };

  return (
    <TableContainer>
      <TableWrapper>
        {loading && (
          <LoadingOverlay>
            <Spinner />
          </LoadingOverlay>
        )}
        
        <StyledTable>
          <thead>
            <tr>
              {columns.map(column => (
                <Th
                  key={column.key}
                  sortable={column.sortable}
                  onClick={() => column.sortable && handleSort(column.key)}
                  style={{zIndex:0}}
                >
                  <CellContent>
                    {column.label}
                    {renderSortIcon(column)}
                  </CellContent>
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.length > 0 ? (
              currentData.map((row, index) => (
                <Tr key={row.id || index}>
                  {columns.map(column => (
                    <Td key={column.key}>
                      {renderCellContent(column, row)}
                    </Td>
                  ))}
                </Tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length}>
                  <EmptyState>
                    {loading ? 'Loading...' : 'No data available'}
                  </EmptyState>
                </td>
              </tr>
            )}
          </tbody>
        </StyledTable>
      </TableWrapper>
      
      {data.length > 0 && (
        <PaginationContainer>
          <PageInfo>
            Showing {startIndex + 1}-{Math.min(endIndex, data.length)} of {data.length} entries
            <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </PageInfo>
          <PaginationButtons>
            {renderPaginationButtons()}
          </PaginationButtons>
        </PaginationContainer>
      )}
    </TableContainer>
  );
};

export default Table; 